'use client';

import { useEffect, useMemo, useState } from 'react';

import { useDevicePermissionState } from '@/context/DevicesInfoContext/useOnDevicePermissionChange';
import { createContext } from '@/context/createContext';
import { getDeviceInfo } from '@/utils/device-utils';

import type { ReactNode } from 'react';

type DevicesInfoContextState = {
  audioInputDevices: null | MediaDeviceInfo[];
  videoInputDevices: null | MediaDeviceInfo[];
  audioOutputDevices: null | MediaDeviceInfo[];

  cameraPermissionState: null | PermissionState;

  microphonePermissionState: null | PermissionState;
  selectedOutputDeviceId: null | string;
  setSelectedOutputDeviceId: (deviceId: string) => void;
};

type DeviceInfoStateValue = Pick<
  DevicesInfoContextState,
  'audioInputDevices' | 'videoInputDevices' | 'audioOutputDevices'
>;

type DeviceInfoState = {
  prev: DeviceInfoStateValue;
  current: DeviceInfoStateValue;
};

const [DevicesInfoContextProviderComp, useDevicesInfoContext] = createContext<DevicesInfoContextState>({
  name: 'DevicesInfoContext',
});
const DevicesInfoContextProvider = ({ children }: { children: ReactNode }) => {
  const [devicesInfo, setDevicesInfo] = useState<DeviceInfoState>({
    prev: {
      audioInputDevices: null,
      videoInputDevices: null,
      audioOutputDevices: null,
    },
    current: {
      audioInputDevices: null,
      videoInputDevices: null,
      audioOutputDevices: null,
    },
  });
  /**
   * Updating selectedOutputDeviceId updates audio element for each participant
   * so that current user can listen others from selected speaker
   */
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string | null>(null);
  const microphonePermissionState = useDevicePermissionState('microphone');
  const cameraPermissionState = useDevicePermissionState('camera');

  const setAvailableDevices = async (callback?: (newState: DeviceInfoState) => void) => {
    const { audioInputDevices, audioOutputDevices, videoInputDevices } = await getDeviceInfo();
    setDevicesInfo(prev => {
      const newState: DeviceInfoState = {
        prev: {
          ...prev.current,
        },
        current: {
          audioInputDevices,
          audioOutputDevices,
          videoInputDevices,
        },
      };
      callback?.(newState);
      return newState;
    });
  };

  useEffect(() => {
    const handleDeviceChange = () => {
      void setAvailableDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    void setAvailableDevices();
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  // This one does not run for Safari since it doesn't support change event for device permission
  // TODO: Safari compatibility
  useEffect(() => {
    void setAvailableDevices();
  }, [microphonePermissionState, cameraPermissionState]);

  const value = useMemo(() => {
    return {
      ...devicesInfo.current,
      microphonePermissionState,
      cameraPermissionState,
      selectedOutputDeviceId,
      setSelectedOutputDeviceId,
    };
  }, [cameraPermissionState, microphonePermissionState, selectedOutputDeviceId, devicesInfo]);

  return <DevicesInfoContextProviderComp value={value}>{children}</DevicesInfoContextProviderComp>;
};

export { useDevicesInfoContext, DevicesInfoContextProvider };
